import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { ArrowUpRight, Menu, X, Sun, Moon } from "lucide-react";
import { useAuth, homeFor } from "@/lib/auth";
import { NAV_LINKS } from "@/lib/content";

export default function MarketingLayout() {
  const { toggleTheme, theme, isAuthed, user } = useAuth();
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const enter = () => nav(isAuthed && user ? homeFor(user) : "/login");

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="orb bg-cobalt-500/50" style={{ width: 520, height: 520, top: -160, left: -180 }} />
      <div className="orb bg-cyanx/40" style={{ width: 460, height: 460, top: 60, right: -120 }} />
      <div className="orb bg-chartreuse/40" style={{ width: 380, height: 380, bottom: -160, left: "35%" }} />
      <div className="absolute inset-0 grid-lines pointer-events-none" />

      <header className="relative z-30 flex items-center justify-between px-4 sm:px-8 lg:px-14 py-4 sm:py-6 safe-top" data-testid="landing-header">
        <Link to="/" className="flex items-center gap-2.5" onClick={() => setMenuOpen(false)}>
          <div className="h-8 w-8 rounded-xl bg-white border border-black/5 grid place-items-center shrink-0 shadow-sm"><img src="/brand/solution-mark-t.png" alt="Solution" className="h-6 w-6 object-contain" /></div>
          <div className="font-display text-xl font-extrabold tracking-tighter">
            Solu<span className="text-cobalt-500">tion</span>
          </div>
        </Link>

        {/* Desktop nav */}
        <div className="hidden sm:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `text-sm font-medium px-3 py-1.5 rounded-full transition-colors hover:text-cobalt-500 ${
                  isActive ? "text-cobalt-500 bg-cobalt-500/10" : ""
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <button
            data-testid="landing-theme-toggle"
            onClick={toggleTheme}
            className="glass rounded-full px-4 py-1.5 text-xs font-medium hover:-translate-y-0.5 transition-transform ml-2"
          >
            {theme === "dark" ? "Light mode" : "Dark mode"}
          </button>
          {isAuthed ? (
            <Link to={homeFor(user)} className="text-sm font-medium hover:text-cobalt-500 ml-1 whitespace-nowrap">
              Continue as {user?.name} &rarr;
            </Link>
          ) : (
            <Link to="/login" className="text-sm font-medium hover:text-cobalt-500 inline-flex items-center gap-1 ml-1 whitespace-nowrap">
              Sign in <ArrowUpRight className="h-4 w-4" />
            </Link>
          )}
        </div>

        {/* Mobile controls */}
        <div className="flex sm:hidden items-center gap-2">
          <button
            aria-label="Toggle theme"
            onClick={toggleTheme}
            className="h-10 w-10 grid place-items-center rounded-full glass tap-shrink"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <button
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="landing-menu-toggle"
            className="h-10 w-10 grid place-items-center rounded-full glass tap-shrink"
          >
            {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </header>

      {/* Mobile menu sheet */}
      {menuOpen && (
        <div className="sm:hidden fixed inset-0 z-20 animate-in fade-in duration-150">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={() => setMenuOpen(false)} />
          <div className="absolute top-0 right-0 left-0 pt-20 px-4 safe-top">
            <div className="glass-strong rounded-2xl p-2 flex flex-col gap-1 shadow-[0_16px_48px_rgba(15,23,42,0.18)]">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `text-base font-medium px-4 py-3 rounded-xl transition-colors tap-shrink ${
                      isActive ? "text-cobalt-500 bg-cobalt-500/10" : "hover:bg-black/5 dark:hover:bg-white/5"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="h-px bg-black/10 dark:bg-white/10 my-1" />
              {isAuthed ? (
                <Link to={homeFor(user)} onClick={() => setMenuOpen(false)} className="text-base font-medium px-4 py-3 rounded-xl hover:bg-black/5 dark:hover:bg-white/5 tap-shrink">
                  Continue as {user?.name} &rarr;
                </Link>
              ) : (
                <Link to="/login" onClick={() => setMenuOpen(false)} className="text-base font-medium px-4 py-3 rounded-xl hover:bg-black/5 dark:hover:bg-white/5 inline-flex items-center gap-1.5 tap-shrink">
                  Sign in <ArrowUpRight className="h-4 w-4" />
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      <main className="relative z-10">
        <Outlet />
      </main>

      {/* FOOTER */}
      <footer className="relative z-10 border-t border-white/40 dark:border-white/10 py-10 px-4 sm:px-8 lg:px-14 safe-bottom">
        <div className="max-w-7xl mx-auto grid sm:grid-cols-[1.5fr_1fr_1fr] gap-8">
          <div>
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-xl bg-white border border-black/5 grid place-items-center shrink-0 shadow-sm"><img src="/brand/solution-mark-t.png" alt="Solution" className="h-6 w-6 object-contain" /></div>
              <div className="font-display text-lg font-extrabold tracking-tighter">Solu<span className="text-cobalt-500">tion</span></div>
            </div>
            <p className="mt-3 text-sm text-muted-foreground max-w-xs">
              A multi-tenant IT service desk for institutions, by Arventiq Labs &mdash; India's innovation lab for cybersecurity and technology.
            </p>
            <a href="https://arventiqlabs.com" target="_blank" rel="noreferrer" className="mt-4 inline-flex flex-col gap-1 opacity-80 hover:opacity-100 transition-opacity">
              <span className="text-[9px] text-mono uppercase tracking-widest text-muted-foreground">Powered by</span>
              <img src="/brand/arventiq-full.png" alt="Arventiq Labs" className="h-6 w-auto dark:brightness-0 dark:invert" />
            </a>
          </div>
          <div>
            <div className="overline mb-3">Product</div>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/features" className="hover:text-cobalt-500">Features</Link></li>
              <li><Link to="/how-it-works" className="hover:text-cobalt-500">How it works</Link></li>
              <li><Link to="/products" className="hover:text-cobalt-500">Product family</Link></li>
              <li><Link to="/security" className="hover:text-cobalt-500">Security</Link></li>
              <li><Link to="/login" className="hover:text-cobalt-500">Sign in</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline mb-3">Arventiq Labs</div>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><a href="https://arventiqlabs.com" target="_blank" rel="noreferrer" className="hover:text-cobalt-500">Home</a></li>
              <li><a href="https://arventiqlabs.com/services.html" target="_blank" rel="noreferrer" className="hover:text-cobalt-500">Services</a></li>
              <li><a href="https://fortifyhub.net" target="_blank" rel="noreferrer" className="hover:text-cobalt-500">FortifyHub</a></li>
              <li><Link to="/contact" className="hover:text-cobalt-500">Contact</Link></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-8 pt-6 border-t border-white/40 dark:border-white/10 flex flex-wrap justify-between gap-2 text-xs text-muted-foreground">
          <div>&copy; 2026 Solution &middot; Arventiq Labs &middot; Bengaluru, India</div>
          <div className="text-mono uppercase tracking-widest">Innovate &middot; Learn &middot; Secure</div>
        </div>
      </footer>
    </div>
  );
}
